/**
 * Onboarding — first member of a family ("Start") or an additional
 * member joining an existing family via invite code ("Join"). Both
 * flows end with a PIN set + an active session.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

export default function OnboardingPage() {
  const [params] = useSearchParams();
  const prefillJoinCode = params.get('join');
  const [mode, setMode] = useState<'choose' | 'start' | 'join'>(
    prefillJoinCode ? 'join' : 'choose',
  );
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  if (inviteCode) {
    return (
      <div className="min-h-screen bg-paper p-6 flex flex-col items-center">
        <div className="max-w-md w-full pt-12">
          <h1 className="font-serif text-3xl text-ink">Family started.</h1>
          <p className="font-serif italic text-soft mt-2">
            Share this code with the rest of your household.
          </p>
          <div className="card mt-8 p-8 text-center">
            <div className="kicker text-soft">INVITE CODE</div>
            <div className="mt-3 font-mono text-4xl tracking-[0.3em] text-oxblood">
              {inviteCode}
            </div>
            <p className="text-soft text-sm mt-4">
              They open Twelve Stones, tap Continue, type this code, pick their name, set
              a PIN.
            </p>
          </div>
          <Link
            to="/today"
            className="block mt-8 w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium flex items-center justify-center"
          >
            Go to Today
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper p-6">
      <div className="max-w-md mx-auto pt-12">
        <h1 className="font-serif text-4xl text-ink">Welcome to Twelve Stones.</h1>
        <p className="text-soft mt-2 italic font-serif text-lg">
          A family's pile of stones.
        </p>

        {mode === 'choose' && (
          <div className="mt-10 space-y-4">
            <button
              onClick={() => setMode('start')}
              className="card w-full p-5 text-left hover:bg-line/40"
            >
              <div className="kicker text-oxblood">First member</div>
              <div className="font-serif text-2xl text-ink mt-1">Start a family</div>
              <p className="text-soft text-sm mt-1">
                Once you're set up, invite the others by email. They tap the link to join.
              </p>
            </button>
            <div className="card w-full p-5">
              <div className="kicker text-sage">Got an email invite?</div>
              <div className="font-serif text-2xl text-ink mt-1">Open the link</div>
              <p className="text-soft text-sm mt-1">
                Tap the link in the email we sent you. It signs you straight in — no
                invite code or PIN needed.
              </p>
              <button
                onClick={() => setMode('join')}
                className="mt-3 text-xs text-soft underline"
              >
                Use the legacy invite code instead
              </button>
            </div>
            <Link to="/" className="block mt-2 text-center text-soft text-sm underline">
              Already set up? Sign in
            </Link>
          </div>
        )}

        {mode === 'start' && (
          <StartFamilyForm
            onBack={() => setMode('choose')}
            onDone={(code) => setInviteCode(code)}
          />
        )}
        {mode === 'join' && (
          <JoinFamilyForm
            prefillCode={prefillJoinCode ?? ''}
            onBack={() => setMode('choose')}
          />
        )}
      </div>
    </div>
  );
}

function StartFamilyForm({
  onBack,
  onDone,
}: {
  onBack: () => void;
  onDone: (inviteCode: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const pin = String(fd.get('pin'));
    const confirm = String(fd.get('confirmPin'));
    if (pin !== confirm) {
      setErr("PINs don't match.");
      setBusy(false);
      return;
    }
    try {
      const res = await api.post<{ inviteCode: string }>('/auth/start-family', {
        familyName: String(fd.get('familyName')),
        displayName: String(fd.get('displayName')),
        role: 'parent',
        pin,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onDone(res.inviteCode);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-8 space-y-4">
      <button type="button" onClick={onBack} className="text-soft text-sm">
        ← back
      </button>
      <Field label="Family name" name="familyName" placeholder="The Crockers" required />
      <Field label="Your name" name="displayName" placeholder="Dad" required />
      <PinField label="Your PIN (4–8 digits)" name="pin" />
      <PinField label="Confirm PIN" name="confirmPin" />
      {err && <p className="text-oxblood text-sm">{err}</p>}
      <button
        type="submit"
        disabled={busy}
        className="w-full h-12 rounded-xl bg-oxblood text-paper font-medium disabled:opacity-50"
      >
        {busy ? 'Setting up…' : 'Start the family'}
      </button>
    </form>
  );
}

function JoinFamilyForm({
  onBack,
  prefillCode,
}: {
  onBack: () => void;
  prefillCode: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const pin = String(fd.get('pin'));
    const confirm = String(fd.get('confirmPin'));
    if (pin !== confirm) {
      setErr("PINs don't match.");
      setBusy(false);
      return;
    }
    try {
      await api.post('/auth/join-family', {
        inviteCode: String(fd.get('inviteCode')).toUpperCase(),
        displayName: String(fd.get('displayName')),
        role: fd.get('role') === 'youth' ? 'youth' : 'parent',
        birthdate: String(fd.get('birthdate')) || undefined,
        pin,
      });
      window.location.href = '/today';
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-8 space-y-4">
      <button type="button" onClick={onBack} className="text-soft text-sm">
        ← back
      </button>
      <Field
        label="Invite code"
        name="inviteCode"
        placeholder="ABCD2345"
        required
        autoCapitalize="characters"
        defaultValue={prefillCode}
      />
      <Field label="Your name" name="displayName" placeholder="" required />
      <div>
        <label className="block text-sm text-soft mb-1">You are…</label>
        <select
          name="role"
          className="w-full h-12 rounded-xl border border-line bg-card px-3"
          defaultValue="parent"
        >
          <option value="parent">A parent</option>
          <option value="youth">A kid (11–15)</option>
        </select>
      </div>
      <Field label="Birthdate (optional)" name="birthdate" type="date" />
      <PinField label="Pick a PIN (4–8 digits)" name="pin" />
      <PinField label="Confirm PIN" name="confirmPin" />
      {err && <p className="text-oxblood text-sm">{err}</p>}
      <button
        type="submit"
        disabled={busy}
        className="w-full h-12 rounded-xl bg-oxblood text-paper font-medium disabled:opacity-50"
      >
        {busy ? 'Joining…' : 'Join the family'}
      </button>
    </form>
  );
}

function Field({
  label,
  name,
  type = 'text',
  ...rest
}: {
  label: string;
  name: string;
  type?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-sm text-soft mb-1">{label}</span>
      <input
        name={name}
        type={type}
        className="w-full h-12 rounded-xl border border-line bg-card px-3 focus:outline-none focus:border-oxblood"
        {...rest}
      />
    </label>
  );
}

function PinField({ label, name }: { label: string; name: string }) {
  return (
    <label className="block">
      <span className="block text-sm text-soft mb-1">{label}</span>
      <input
        name={name}
        type="password"
        inputMode="numeric"
        pattern="\d{4,8}"
        maxLength={8}
        required
        className="w-full h-12 rounded-xl border border-line bg-card px-3 text-xl tracking-[0.3em] font-mono focus:outline-none focus:border-oxblood"
      />
    </label>
  );
}
